import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { CloudService, ServiceCardData } from '../../core/services/cloud.service';
import { GcpConnection } from '../../core/services/gcp.service';
import { GCP_SERVICES } from '../../core/cloud-catalog';

export type GcpOverviewState = 'loading' | 'not-connected' | 'connected' | 'error';

/**
 * GCP Cloud overview body — the GCP counterpart to the AWS/Azure card grid.
 *
 * Presentational: the parent shell owns the GCP connection + the active
 * project/region scope (bound to ?account=/?region=, exactly like AWS/Azure)
 * and passes them down. This component renders the service cards, loads each
 * card's live resource count independently for the active scope, and derives an
 * intentional per-card status (Healthy when resources exist, Idle when a
 * connected service simply has none — never a blanket Unknown). Cards navigate
 * to their real detail pages.
 */
@Component({
  selector: 'app-gcp-overview',
  templateUrl: './gcp-overview.component.html',
})
export class GcpOverviewComponent implements OnChanges, OnDestroy {
  /** Connection + scope, owned by the shell (single source of truth). */
  @Input() connection: GcpConnection | null = null;
  @Input() state: GcpOverviewState = 'loading';
  @Input() project = '';
  @Input() region = 'all';
  @Input() monitoringLoaded = false;
  @Input() monitoredCount = 0;

  cards: ServiceCardData[] = GCP_SERVICES.map(s => ({
    id: s.id, name: s.name, icon: s.icon, route: s.route, status: 'idle',
    metrics: [{ label: s.metricLabel, value: '—' }],
    ...(s.scope ? { scope: s.scope } : {}),
  }));

  private readonly loadingCards = new Set<string>();
  private countsSub?: Subscription;
  /** Bumped on every scope load so a late response from a prior scope is dropped. */
  private loadGeneration = 0;

  constructor(readonly cloudService: CloudService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // (Re)load counts whenever the active scope changes or we (re)enter the
    // connected state. The shell sets the cloud service's account/scope before
    // these inputs propagate, so getGcpResources() reads the correct scope.
    const scopeChanged = changes['project'] || changes['region'] || changes['state'];
    if (scopeChanged && this.state === 'connected' && this.project) {
      this.loadCounts();
    }
  }

  /**
   * Per-card resource counts from ONE batched backend query (not one request per
   * card) — a single GCP discovery call for the whole grid.
   */
  private loadCounts(): void {
    const generation = ++this.loadGeneration;
    this.countsSub?.unsubscribe();
    this.loadingCards.clear();
    GCP_SERVICES.forEach(s => this.loadingCards.add(s.id));
    // Reset to placeholders so no stale previous-scope numbers linger.
    this.cards = this.cards.map(c => ({ ...c, status: 'idle', metrics: [{ ...c.metrics[0], value: '—' }] }));

    this.countsSub = this.cloudService.getGcpResourceCounts().subscribe(counts => {
      if (generation !== this.loadGeneration) return; // stale scope — ignore
      this.cards = this.cards.map(c => {
        const svc = GCP_SERVICES.find(s => s.id === c.id);
        const count = svc ? (counts[svc.serviceType] ?? 0) : 0;
        return { ...c, status: count > 0 ? 'healthy' : 'idle', metrics: [{ ...c.metrics[0], value: String(count) }] };
      });
      this.loadingCards.clear();
    });
  }

  isCardLoading(id: string): boolean { return this.loadingCards.has(id); }

  get projectLabel(): string {
    const projects = this.connection?.projects ?? [];
    const active = projects.find(p => p.projectId === this.project)
      ?? projects.find(p => p.isPrimary) ?? projects[0];
    return active ? (active.label || active.projectId) : '—';
  }

  ngOnDestroy(): void {
    this.countsSub?.unsubscribe();
  }
}
